<template>
  <div class="flex flex-column gap-2">
    <label v-if="label" class="editor-label">{{ label }}</label>

    <prism-editor
      class="my-editor"
      v-model="code"
      :highlight="highlighter"
      line-numbers
    ></prism-editor>

    <div class="text-sm mb-2">
      <i class="pi pi-info-circle info-text"></i>
      Para insertar una variable en la sentencia SQL, debe usar la sinxatis
      <i>$variable</i>. Si quieres insertar una variable que se filtre con un
      <i>like</i>, por ejemplo, debes usar la sintaxis <i>'$%variable%'</i>.
    </div>

    <small v-if="error" class="text-red-400">{{ error }}</small>

    <div class="flex align-items-center gap-2">
      <Button
        label="Comprobar petición"
        icon="pi pi-wifi"
        @click="testRequest()"
        severity="warning"
        :loading="loading"
        loading-icon="pi pi-spinner"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from "vue";
import { useStore } from "vuex";
import Button from "primevue/button";
import { dataSourceCheckQueryService } from "@/api/data-sources/data-sources.api.js";
import { useToast } from "primevue/usetoast";

// import Prism Editor
import { PrismEditor } from "vue-prism-editor";
import "vue-prism-editor/dist/prismeditor.min.css"; // import the styles

// import highlighting library (you can use any library you want just return html string)
import { highlight, languages } from "prismjs/components/prism-core";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-sql";
import "prismjs/themes/prism-tomorrow.css"; // import syntax highlighting styles

// #region Props, Emits
const props = defineProps({
  label: {
    type: String,
    required: false,
  },
  defaultCode: {
    type: String,
    required: false,
  },
  dataSourceId: {
    type: Number,
    required: false,
  },
  filters: {
    type: Array,
    required: false,
    default: () => [],
  },

});
// #endregion

// #region Params, Ref, Reactives, Computed
const loading = ref(false);
const code = ref("");
const error = ref("");
const toast = useToast();
const store = useStore();
// #endregion

// #region Functions
onMounted(() => {
  code.value = props.defaultCode;
});

function highlighter(code) {
  return highlight(code, languages.sql);
}

// Test the conection with the form data
async function testRequest() {
  // If the form is invalid, show a error toast
  if (!code.value) {
    error.value = "El código SQL no puede estar vacío";

    return;
  }

  if (props.dataSourceId == null) {
    error.value = "No ha seleccionado una fuente de datos";

    return;
  }

  loading.value = true;

  error.value = "";

  toast.add({
    severity: "info",
    summary: "Comprobando petición",
    detail: "Esto puede tardar unos segundos",
    closable: true,
    life: 3000,
  });

  
  let naturalQuery = code.value;

  if (props.filters.length > 0) {
    props.filters.forEach(filter => {
      if (!filter.variable) return;

      const clase = filter.claseFiltroDto?.nombreClaseFiltro;
      let variableName;
      
      if (clase === "Valor de rango") {
        try {
          const variableArray = typeof filter.variable === 'string' 
            ? JSON.parse(filter.variable) 
            : filter.variable;
          
          if (Array.isArray(variableArray)) {
            const variableObj = variableArray.find(v => v.value !== null);
            variableName = variableObj ? variableObj.value : "";
          }
        } catch (e) {
          console.error('Error parsing variable:', e);
          return;
        }
      } else {
        variableName = filter.variable;
      }
      
      if (!variableName) return;
      
      variableName = variableName.replace(/'/g, "");
      const escapedVariable = variableName.replace(/\$/g, "\\$");

      const variableRegex = new RegExp(
        `${escapedVariable}(?![a-zA-Z0-9_])`,
        "g"
      );

      let isBetweenContext = false;
      
      if (clase === "Valor de rango") {
        const betweenPattern = new RegExp(
          `BETWEEN\\s+${escapedVariable}(?![a-zA-Z0-9_])`,
          'i'
        );
        isBetweenContext = betweenPattern.test(code.value);
      }

      const replacement = getValueForContext(
        filter,
        code.value,
        isBetweenContext
      );
      naturalQuery = naturalQuery.replace(variableRegex, replacement);
    });
  }

  const response = await dataSourceCheckQueryService({
    idFuenteDatos: props.dataSourceId,
    query: naturalQuery,
  });

  if (response.data) {
    toast.add({
      severity: "success",
      summary: "Conexión correcta",
      detail: "La conexión se ha realizado correctamente",
      closable: true,
      life: 3000,
    });
  } else {
    toast.add({
      severity: "error",
      summary: "Conexión incorrecta",
      detail: "No se ha podido realizar la conexión",
      closable: true,
      life: 3000,
    });
  }

  loading.value = false;
}

function getValueForContext(filtro, query, isBetweenContext = false) {
  const variable = filtro.variable;

  const isQuotedContext =
    query.includes(`'${variable}'`) ||
    query.includes(`'%${variable}`) ||
    query.includes(`${variable}%'`);

  if (isQuotedContext) {
    return getVariableRawByType(filtro);
  }

  return getVariableByType(filtro, isBetweenContext);
}

function getVariableByType(filtro, isBetweenContext = false) {
  const type = filtro.tipoFiltroDto?.nombreTipoFiltro?.toLowerCase();
  const valores = filtro.valorPredeterminado ?? [];
  const clase = filtro.claseFiltroDto?.nombreClaseFiltro;

  if (!valores.length) return "";

  switch (clase) {
    case "Valor único":
      return formatValue(valores[0], type);

    case "Lista de valores":
      return valores
        .map(v => formatValue(v, type))
        .join(", ");

    case "Valor de rango":
      if (isBetweenContext && valores.length === 2) {
        const min = formatValue(valores[0], type);
        const max = formatValue(valores[1], type);
        return `${min} AND ${max}`;
      }
      return "";

    default:
      return formatValue(valores[0], type);
  }
}


function getVariableRawByType(filtro) {
  const valores = filtro.valorPredeterminado ?? [];
  const clase = filtro.claseFiltroDto?.nombreClaseFiltro;

  if (!valores.length) return "";

  switch (clase) {
    case "Valor único":
      return valores[0];

    case "Lista de valores":
      return valores.join(", ");

    case "Valor de rango":
      return "";

    default:
      return valores[0];
  }
}

function formatValue(value, type) {
  if (type === "texto") return `'${value}'`;
  if (type === "fecha") return `'${new Date(value).toISOString()}'`;
  return value;
}

function loadCode(codeString) {
  code.value = codeString;
}
// #endregion

// #region Effects, Hooks
watch(code, (value) => {
  store.commit("setSqlCode", value);
});

// watch props.code
watch(
  () => props.defaultCode,
  (value) => {
    code.value = value;
  }
);
// #endregion

// #region Provide, Expose
defineExpose({
  loadCode,
  code,
  error,
});
// #endregion
</script>

<style lang="scss" scoped>
@import "@/styles/_vars.scss";

.my-editor {
  background: #2d2d2d;
  color: #ccc;
  font-family: Fira code, Fira Mono, Consolas, Menlo, Courier, monospace;
  font-size: 14px;
  line-height: 1.5;
  padding: 5px;
  border-radius: 0.5rem !important;
}

// optional
.prism-editor__textarea:focus {
  outline: none;
}

.editor-label {
  color: #6b7280;
}

.info-text {
  transform: translateY(2px);
}
</style>
